import { Icon } from "@components/library";
import { COLORS, FONTS } from "@constants";
import resizeGrabber from "@images/icons/resize_grabber.svg";
import { t } from "@utils/i18n";
import { ChangeEvent, ForwardedRef, ReactNode, forwardRef } from "react";
import styled, { css } from "styled-components";
import FormLabel from "../Texts/FormLabel";

export type TextInputError = { hasError: boolean; errorMessage: string };

interface Props {
  id?: string;
  value: string;
  onChange:
    | ((e: ChangeEvent<HTMLInputElement>) => void)
    | ((e: ChangeEvent<HTMLTextAreaElement>) => void);
  name?: string;
  label?: ReactNode;
  labelFont?: string;
  placeholder?: string;
  placeholderColor?: string;
  onFocus?: (...args: any[]) => void;
  onBlur?: (...args: any[]) => void;
  optional?: boolean;
  characterLimit?: number;
  warningLimit?: number;
  errors?: TextInputError[];
  textarea?: boolean;
  rows?: number; // applicable if textarea
  type?: string;
  hideClearButton?: boolean;
  resize?: string;
  disabled?: boolean;
  startingHeight?: string;
  min?: number;
  max?: number;
  maxLength?: number;
  hasPassedStep?: boolean; // used for showing error states in forms
  "data-testid"?: string;
  className?: string;
}

const TextInput = (
  {
    id,
    value,
    onChange,
    name,
    label,
    labelFont = FONTS.SEMIBOLD_2,
    placeholder,
    placeholderColor = COLORS.NEUTRAL_400,
    type = "text",
    onFocus,
    onBlur,
    optional = false,
    characterLimit,
    warningLimit,
    errors,
    hideClearButton = false,
    textarea = false,
    rows,
    resize = "vertical",
    disabled,
    startingHeight = "84px",
    min,
    max,
    maxLength,
    hasPassedStep,
    "data-testid": dataTestId,
    className,
  }: Props,
  ref: ForwardedRef<HTMLTextAreaElement | HTMLInputElement>
) => {
  const hasMissingRequiredError = Boolean(!optional && hasPassedStep && value.trim().length === 0);
  const hasExceededCharacterLimit = Boolean(characterLimit && value.length > characterLimit);
  const hasExceededWarningLimit = warningLimit && value.length > warningLimit;
  const hasAnyError =
    Boolean(errors?.map((error) => error.hasError).includes(true)) ||
    hasMissingRequiredError ||
    hasExceededCharacterLimit;

  const handleClear = (e) => {
    const newEvent = {
      ...e,
      target: {
        ...e.target,
        value: type === "number" ? undefined : "",
      },
    };

    onChange(newEvent);
  };

  const input = textarea ? (
    <TextArea
      id={id}
      autoComplete="off"
      name={name}
      placeholder={placeholder}
      placeholderColor={placeholderColor}
      value={value}
      onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
        (onChange as (e: ChangeEvent<HTMLTextAreaElement>) => void)(e)
      }
      onFocus={onFocus}
      onBlur={onBlur}
      ref={ref as ForwardedRef<HTMLTextAreaElement>}
      startingHeight={startingHeight}
      rows={rows}
      disabled={disabled}
      resize={resize}
      hasError={hasAnyError}
      maxLength={maxLength}
      data-testid={dataTestId}
    />
  ) : (
    <InputContainer hasError={hasAnyError}>
      <Input
        id={id}
        type={type}
        autoComplete="off"
        name={name}
        placeholder={placeholder}
        placeholderColor={placeholderColor}
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          (onChange as (e: ChangeEvent<HTMLInputElement>) => void)(e)
        }
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={disabled}
        ref={ref as ForwardedRef<HTMLInputElement>}
        hasError={hasAnyError}
        hideClearButton={hideClearButton}
        min={min}
        max={max}
        maxLength={maxLength}
        data-testid={dataTestId}
      />
      {value && !hideClearButton && (
        <ClearButton type="button" onClick={(e) => handleClear(e)}>
          <Icon name="X" size="xs" />
        </ClearButton>
      )}
    </InputContainer>
  );

  let labelGap = 8; // px
  if (labelFont.includes("font-size: 16px;")) {
    labelGap = 16; // px
  }

  const errorsWithRequiredError = [
    ...(errors ?? []),
    {
      hasError: hasMissingRequiredError,
      errorMessage: t("error.required_field"),
    },
  ];

  const errorIcon = <Icon name="Attention" size="sm" margin="0 4px 0 0" color={COLORS.RED_500} />;

  return (
    <Container className={className}>
      {label ? (
        <Label gap={labelGap}>
          <FormLabel label={label} font={labelFont} hasError={hasAnyError} isOptional={optional} />
          {input}
        </Label>
      ) : (
        input
      )}
      {characterLimit && (
        <CharacterLimit
          hasError={Boolean(hasExceededCharacterLimit)}
          hasWarning={Boolean(hasExceededWarningLimit)}
        >
          {hasExceededCharacterLimit && errorIcon}
          {value?.length} of {characterLimit} characters including spaces
        </CharacterLimit>
      )}
      {errorsWithRequiredError.map(
        (error) =>
          error.hasError && (
            <ErrorRow key={error.errorMessage} data-testid={`${dataTestId}-error`}>
              {errorIcon}
              {error.errorMessage}
            </ErrorRow>
          )
      )}
    </Container>
  );
};

export default forwardRef(TextInput);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;
// These styles are also used in MultiTextInput and FundingEditModal
export const Label = styled.label<{ gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ gap }) => gap}px;
  margin: 0;
`;
const inputStyles = css<{ hasError?: boolean; placeholderColor?: string }>`
  ${FONTS.REGULAR_2};
  border: 1px solid ${({ hasError }) => (hasError ? COLORS.RED_500 : COLORS.NEUTRAL_300)};
  border-radius: 6px;
  width: 100%;
  ${({ hasError }) => hasError && `background-color: ${COLORS.RED_50}`};
  &::placeholder {
    ${FONTS.REGULAR_2};
    ${({ placeholderColor }) =>
      placeholderColor
        ? `
      opacity: 1;
      color: ${placeholderColor};
    `
        : `
      color: ${COLORS.NEUTRAL_400};
    `}
  }
  &:focus {
    border: 1px solid ${({ hasError }) => (hasError ? COLORS.RED_500 : COLORS.HALO_BLUE)};
  }
`;
const TextArea = styled.textarea<{
  hasError?: boolean;
  resize?: string;
  disabled?: boolean;
  startingHeight?: string;
  rows?: number;
  placeholderColor?: string;
  maxLength?: number;
  ref?: ForwardedRef<HTMLTextAreaElement>;
}>`
  ${inputStyles};
  padding: 6px 10px;
  ${({ rows, startingHeight }) => typeof rows === "undefined" && `height: ${startingHeight}`};
  min-height: 38px;
  resize: ${({ disabled, resize }) => (disabled ? "none" : resize)};
  &::-webkit-resizer {
    background-image: url(${resizeGrabber});
    background-repeat: no-repeat;
    background-size: 100%;
    background-position: bottom right;
  }
  &::-webkit-scrollbar-corner {
    height: unset;
  }
  &:hover:not(:focus) {
    border: 1px solid ${({ hasError }) => (hasError ? COLORS.RED_500 : COLORS.NEUTRAL_500)};
  }
  ${({ disabled }) =>
    disabled &&
    `
    color: ${COLORS.NEUTRAL_500};
    background-color: ${COLORS.NEUTRAL_50};
    &:hover, &:hover:not(:focus) {
      border: 1px solid ${COLORS.NEUTRAL_300};
    }
  `}
`;
const InputContainer = styled.div<{ hasError?: boolean }>`
  position: relative;
  &:hover input:not(:focus) {
    border: 1px solid ${({ hasError }) => (hasError ? COLORS.RED_500 : COLORS.NEUTRAL_500)};
  }
`;
const Input = styled.input<{
  hasError?: boolean;
  hideClearButton?: boolean;
  placeholderColor?: string;
  maxLength?: number;
}>`
  ${inputStyles};
  height: 36px;
  padding: ${({ hideClearButton }) => (hideClearButton ? "6px 10px" : "6px 36px 6px 10px")};
`;
const ClearButton = styled.button`
  position: absolute;
  top: calc(50% - 8px);
  right: 15px;
  border: none;
  background: none;
  padding: 0;
  &:hover {
    opacity: 0.5;
  }
  &:focus {
    outline: none;
  }
`;
const CharacterLimit = styled.div<{ hasError?: boolean; hasWarning?: boolean }>`
  display: flex;
  ${({ hasError, hasWarning }) => (hasError || hasWarning ? FONTS.MEDIUM_3 : FONTS.REGULAR_3)};
  color: ${({ hasError, hasWarning }) => {
    if (hasError) return COLORS.RED_500;
    else if (hasWarning) return COLORS.ORANGE;
    return COLORS.NEUTRAL_500;
  }};
`;
const ErrorRow = styled.div`
  display: flex;
  ${FONTS.MEDIUM_3};
  color: ${COLORS.RED_500};
`;
